import { SuspenseSelect } from '@/components/Suspense';
import {
  BaseSelectProps,
  useSetCacheQueryData,
} from '@/context/SelectComponent/Base';
import { useModelsToOptionsWithCacheItems } from '@/hooks/useTypeToOptions';
import { useTranslation } from '@/i18n';
import { getPageData } from '@/utils/getPageData';
import {
  FormSelect,
  FormSelectProps,
  SelectProps,
} from '@moneyforward/ap-frontend-components';

import {
  ExItem as ExItemModel,
  useGetExItemsSuspenseInfinite,
} from 'ap-openapi';
import {
  forwardRef,
  memo,
  ReactElement,
  Ref,
  startTransition,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FieldPath,
  FieldValues,
  Path,
  PathValue,
  useFormContext,
  useWatch,
} from 'react-hook-form';

export type ExItemRef = {
  items: ExItemModel[];
  selectedItem: () => ExItemModel | undefined;
};

export type Props<TFieldValues extends FieldValues> = {
  selectProps?: Omit<
    FormSelectProps<TFieldValues>['selectProps'],
    'options' | 'comboBox'
  >;
  query?: string;
  per?: number;
  deptId?: string;
  validateMaxCount?: number;
  comboBox?: boolean;
  deptIdName?: FieldPath<TFieldValues>;
  setLabelField?: (label: string) => void;
} & Omit<FormSelectProps<TFieldValues>, 'selectProps' | 'label'> &
  BaseSelectProps;

type InnerExItemSelectProps<TFieldValues extends FieldValues> =
  Props<TFieldValues>;

const BaseExItemSelect = <TFieldValues extends FieldValues>(
  {
    selectProps: _selectProps,
    defaultOptions: _defaultOptions,
    deptId,
    onChangeAfter,
    setLabelField,
    cacheQueryKey,
    per = 100,
    comboBox = true,
    ...rest
  }: InnerExItemSelectProps<TFieldValues>,
  ref: Ref<ExItemRef>
) => {
  const { control, name } = rest;
  const { getValues, setValue } = useFormContext<TFieldValues>();
  const defaultOptions = useMemo(
    () => _defaultOptions ?? [],
    [_defaultOptions]
  );
  const { t } = useTranslation();
  const [query, setQuery] = useState<string | undefined>(undefined);
  const {
    data: apiData,
    error,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetched,
  } = useGetExItemsSuspenseInfinite(
    {
      query,
      dept_id: deptId,
      per: per,
    },
    {
      query: {
        getNextPageParam: (page) => {
          // FIXME: OpenAPIの間違い？
          if ('next' in page.data && page.data.next) {
            // @ts-ignore
            const url = new URL(page.data.next ?? '/', location.href);
            if (!url.searchParams.has('page')) return undefined;
            return Number(url.searchParams.get('page'));
          } else {
            if (page.data.pagination) {
              if (page.data.pagination.has_next_page)
                return (page.data.pagination.page ?? 1) + 1;
            }
          }
          return undefined;
        },
      },
    }
  );
  if (error) throw error;
  const data = useMemo(() => getPageData(apiData, 'ex_items'), [apiData]);
  const options = useModelsToOptionsWithCacheItems<ExItemModel>(
    data,
    cacheQueryKey,
    query,
    defaultOptions,
    {
      labelKeys: ['name'],
      value: 'id',
    }
  );
  const fetchMore = useCallback(
    async (callback: () => void) => {
      if (hasNextPage) {
        await fetchNextPage();
      }
      callback();
    },
    [fetchNextPage, hasNextPage]
  );
  const onSearch = useCallback((value: string) => {
    startTransition(() => {
      setQuery(value);
    });
  }, []);
  const selectProps: SelectProps = useMemo(() => {
    return {
      onSearchDebounceWait: 600,
      comboBox: comboBox,
      placeholder: t('ex_item_placeholder'),
      inputPlaceholder: t('ex_item_input_placeholder'),
      ..._selectProps,
      options: options,
      onSearch: onSearch,
      fetchMore: fetchMore,
      loading: isLoading,
      mode: 'single',
      selectAll: undefined,
      selectAllLabel: undefined,
    };
  }, [comboBox, t, _selectProps, options, onSearch, fetchMore, isLoading]);

  useImperativeHandle(
    ref,
    () => ({
      items: data,
      selectedItem: () => {
        const selectedId = getValues(name);
        return data.find((item) => item.id === selectedId);
      },
    }),
    [data, getValues, name]
  );
  const id = useWatch<TFieldValues>({ name, control });
  const prevDeptIdRef = useRef<string | undefined>(deptId);
  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    // do only first render
    if (isFetched && isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      prevDeptIdRef.current = deptId;
      return;
    }

    // check if deptId changed
    const deptIdChanged = prevDeptIdRef.current !== deptId;
    prevDeptIdRef.current = deptId;

    // When department changes and data is fetched
    if (isFetched && deptId && deptIdChanged) {
      // Clear search query and trigger onSearch to clear the Select input
      setQuery('');
      const selectedItem = data.find((item) => item.id === id);
      // if no selected item or data is empty, set value to undefined mean clear
      if (!selectedItem || data.length === 0) {
        setValue(
          name,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          '' as PathValue<TFieldValues, Path<TFieldValues>>,
          {
            shouldValidate: true,
          }
        );
        setLabelField?.('');
      }
    }
  }, [
    data,
    deptId,
    hasNextPage,
    id,
    isFetched,
    name,
    options,
    setLabelField,
    setValue,
  ]);

  useSetCacheQueryData(cacheQueryKey, data);

  const wrapOnChangeAfter = useCallback(
    (value: string | number | (string | number)[]) => {
      const selectedOption = options.find(
        (option) => option.value === String(value)
      );
      onChangeAfter?.(value);
      setLabelField?.(selectedOption?.label ?? '');
    },
    [setLabelField, options, onChangeAfter]
  );

  const selectKey = useMemo(() => {
    setQuery('');
    return `ex-item-select-${deptId ?? 'no-dept'}`;
  }, [deptId]);

  return (
    <FormSelect<TFieldValues>
      key={selectKey}
      selectProps={selectProps}
      {...rest}
      onChangeAfter={wrapOnChangeAfter}
    />
  );
};
BaseExItemSelect.displayName = 'BaseExItemSelect';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const ForwardExItemSelect = forwardRef(BaseExItemSelect) as <
  TFieldValues extends FieldValues
>(
  props: InnerExItemSelectProps<TFieldValues> & { ref?: React.Ref<ExItemRef> }
) => ReactElement | null;

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const InnerExItemSelect = forwardRef(function InnerExItemSelect<
  TFieldValues extends FieldValues
>(props: InnerExItemSelectProps<TFieldValues>, ref: React.Ref<ExItemRef>) {
  return (
    <SuspenseSelect>
      <ForwardExItemSelect {...props} ref={ref} />
    </SuspenseSelect>
  );
}) as <TFieldValues extends FieldValues>(
  props: InnerExItemSelectProps<TFieldValues> & { ref?: React.Ref<ExItemRef> }
) => ReactElement | null;

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const ExItemSelect = memo(InnerExItemSelect) as typeof InnerExItemSelect;
